import { useMemo, useState, useRef } from 'react';
import {
	Grid,
	Box,
	Popover,
	Typography,
	IconButton,
	FormControl,
	FormLabel,
	FormControlLabel,
	RadioGroup,
	Radio
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// Our Components
import AutocompleteTextinput from 'components/Input/AutocompleteTextinput';
import LoadingSkeleton from 'components/Frame/LoadingSkelton';
import LoanApplicationDropdown from 'components/Dropdown/lender/LoanApplicationDropdown';

// Our Hooks
import useGetAllAdvisors from 'hooks/advisors/useGetAllAdvisors';
import useGetClients from 'hooks/clients/useGetClients';
import useGetLoanApplicationsByUserId from 'hooks/pylon/useGetLoanApplicationsByUserId';

const breakpointColumns = {
	xs: 12,
	md: 6,
	lg: 4,
	xl: 3
};

const loadingSkeletonSx = {
	width: '100%',
	height: 100
};

function SelectClientViaAdvisor({
	// Client State
	selectedClient,
	setSelectedClient,

	// Advisor State
	selectedAdvisor,
	setSelectedAdvisor,

	// Loan Application State
	selectedLoanApplication,
	setSelectedLoanApplication,

	// Selector
	loanSelector,

	// Dropdown Labels
	labels,
	helperTexts,

	// Children
	children
}) {
	// Client State
	const [clientSearchName, setClientSearchName] = useState('');

	// Filter State
	const [filterBy, setFilterBy] = useState('fullName');

	// Advisor State
	const [advisorSearchName, setAdvisorSearchName] = useState('');
	const [popoverOpen, setPopoverOpen] = useState(false);

	// Ref for the popover anchor
	const anchorEl = useRef(null);

	const {
		isLoading: isGetAdvisorsLoading,
		data,
		isSuccess: isGetAdvisorsSuccess
	} = useGetAllAdvisors(filterBy);

	// Hooks fetching Data
	const { data: clients, isLoading } = useGetClients(
		!!selectedAdvisor,
		selectedAdvisor?.id
	);

	const {
		data: loanApplications,
		isLoading: isGetLoanApplicationsByUserIdLoading,
		isSuccess: isGetLoanApplicationsByUserIdSuccess
	} = useGetLoanApplicationsByUserId(
		selectedClient?.id,
		!!selectedClient,
		loanSelector
	);

	// Creates a label set for each client. Sets these labels to an array of "items", which is used to populate the options within the client selection dropdown
	const listOfAdvisors = useMemo(() => {
		if (isGetAdvisorsSuccess) {
			const advisorList = data.map((currentAdvisor) => {
				const { advisorId } = currentAdvisor;

				const currentFilterChoice = currentAdvisor[filterBy];

				return {
					name: currentFilterChoice,
					id: advisorId,
					fullName: currentAdvisor.invitedName,
					email: currentAdvisor.email
				};
			});

			return advisorList;
		}

		return [];
	}, [data, filterBy, isGetAdvisorsSuccess]);

	const listOfClients = useMemo(() => {
		if (clients) {
			return clients.map((currentClient) => {
				return {
					name: currentClient.invitedName,
					email: currentClient.invitedEmail,
					id: currentClient.clientId
				};
			});
		}

		return [];
	}, [clients]);

	const handleCleanUp = () => {
		setAdvisorSearchName('');
		setSelectedAdvisor(null);
		setSelectedClient(null);
	};

	const handleAdvisorSelect = (
		currentSelectedAdvisor,
		updateSelectedAdvisor
	) => {
		updateSelectedAdvisor(currentSelectedAdvisor);
		setSelectedAdvisor(currentSelectedAdvisor);

		if (selectedClient) {
			setSelectedClient(null);
		}
	};

	const handleAdvisorChange = (incomingValue) => {
		if (incomingValue === '') {
			handleCleanUp();
		}
		setAdvisorSearchName(incomingValue);
	};

	const handleClientChange = (incomingValue) => {
		if (incomingValue === '') {
			setClientSearchName('');
			setSelectedClient(null);
			setSelectedLoanApplication('');
		}
		setClientSearchName(incomingValue);
	};

	const handleClientSelect = (
		currentSelectedClient,
		updateSelectedClient
	) => {
		updateSelectedClient(currentSelectedClient);
		setSelectedClient(currentSelectedClient);
	};

	if (isGetAdvisorsLoading) {
		return <Typography>Loading...</Typography>;
	}

	const hasClients = clients && clients.length > 0;
	const isSelectLoanApplicationReady = hasClients && selectedClient;
	const hasLoanApplications =
		isGetLoanApplicationsByUserIdSuccess &&
		loanApplications.loanApplications.length > 0;

	return (
		<>
			<Grid container spacing={2} sx={{ marginBottom: 4 }}>
				<Grid item {...breakpointColumns}>
					{isGetAdvisorsSuccess && listOfAdvisors.length > 0 && (
						<>
							<AutocompleteTextinput
								label="Start by selecting an advisor"
								forcePopupIcon
								freeSolo={false}
								getOptionLabel={({ name }) => name}
								helperText="Selecting an advisor will show you their clients"
								handleChange={handleAdvisorChange}
								handleSelect={handleAdvisorSelect}
								value={advisorSearchName}
								optionList={listOfAdvisors}
								withoutFilter={false}
								renderOption={(props, option) => (
									<li {...props} key={option.id}>
										{option.name}
									</li>
								)}
								InputAdornment={
									<IconButton
										ref={anchorEl}
										onClick={() => setPopoverOpen(true)}
									>
										<FilterAltIcon />
									</IconButton>
								}
							/>
							<Popover
								sx={{ marginLeft: 5 }}
								open={popoverOpen}
								anchorEl={anchorEl.current}
								onClose={() => {
									setPopoverOpen(false);
								}}
							>
								<FormControl sx={{ padding: 2 }}>
									<FormLabel id="demo-radio-buttons-group-label">
										Filter by
									</FormLabel>
									<RadioGroup
										aria-labelledby="demo-radio-buttons-group-label"
										name="radio-buttons-group"
										onChange={(e) => {
											setFilterBy(e.target.value);
											setPopoverOpen(false);
											setSelectedAdvisor(null);
											setAdvisorSearchName('');
										}}
										value={filterBy}
									>
										<FormControlLabel
											value="fullName"
											control={<Radio />}
											label="Filter by advisor full name"
										/>
										<FormControlLabel
											value="email"
											control={<Radio />}
											label="Filter by advisor email"
										/>
									</RadioGroup>
								</FormControl>
							</Popover>
						</>
					)}
				</Grid>
				<Grid item {...breakpointColumns}>
					{isLoading && <LoadingSkeleton sx={loadingSkeletonSx} />}

					{selectedAdvisor && !isLoading && !hasClients && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								height: '100%',
								paddingLeft: 5
							}}
						>
							<Typography sx={{ display: 'block' }}>
								No clients found
							</Typography>
						</Box>
					)}
					{selectedAdvisor && hasClients && (
						<>
							<AutocompleteTextinput
								label={labels.selectClientLabel}
								helperText={helperTexts.selectClientHelperText}
								inputSx={{ padding: 1 }}
								forcePopupIcon
								freeSolo={false}
								getOptionLabel={({ name }) => name}
								handleChange={handleClientChange}
								handleSelect={handleClientSelect}
								value={clientSearchName}
								optionList={listOfClients}
								withoutFilter={false}
								renderOption={(props, option) => (
									<li {...props} key={option.id}>
										{option.name}
									</li>
								)}
							/>
						</>
					)}
				</Grid>
				<Grid item {...breakpointColumns}>
					{isSelectLoanApplicationReady &&
						isGetLoanApplicationsByUserIdLoading && (
							<LoadingSkeleton sx={loadingSkeletonSx} />
						)}

					{isSelectLoanApplicationReady &&
						isGetLoanApplicationsByUserIdSuccess &&
						hasLoanApplications && (
							<LoanApplicationDropdown
								label={labels.selectLoanApplicationLabel}
								helperText={
									helperTexts.selectLoanApplicationHelperText
								}
								selected={selectedLoanApplication}
								onChange={setSelectedLoanApplication}
								loanApplications={loanApplications.loanApplications.map(
									({ id, creationDate }) =>
										`${creationDate} - ${id}`
								)}
							/>
						)}
				</Grid>
				{children}
			</Grid>
		</>
	);
}

SelectClientViaAdvisor.defaultProps = {
	labels: {
		selectClientLabel: 'Select a client',
		selectLoanApplicationLabel: 'Optionally select a loan application'
	},
	helperTexts: {
		selectClientHelperText:
			'Selecting a client will start a loan application',
		selectLoanApplicationHelperText:
			'This allows you to resume a loan application'
	},
	loanSelector: (data) => data
};

export default SelectClientViaAdvisor;
